<template>
  <div class="SelectHours">
    <v-chip
      v-for="(hour, index) in rangeTimeComputed"
      :key="hour.value"
      @click="updateRange(index, hour.value)"
      class="SelectHours-hour"
      :dark="isActiveHour(hour.value)"
      :color="isActiveHour(hour.value) ? activeColor : ''"
      :disabled="rangeTimeComputedUnSelectable.some(disable => disable.value === hour.value)"
    >
      {{ hour.display }}
    </v-chip>
  </div>
</template>

<script>
import { rangeTime } from '@/services/time'

export default {
  name: 'SelectHours',

  model: {
    prop: 'ranges',
    event: 'change'
  },

  props: {
    hour: Boolean,
    increment: {
      type: Number,
      default: 1
    },
    openings: {
      type: Array,
      validator(openings) {
        return openings.every(opening => Array.isArray(opening) && opening.every(hour => typeof hour === 'number'))
      }
    },
    ranges: {
      type: Array,
      validator(ranges) {
        const isOrder = ranges.every(([beginHour], index, array) => {
          if (index === 0) return true
          return beginHour > array[index - 1][0]
        })
        if (!isOrder) console.error('You have to provide an ordered list')

        return isOrder && ranges.every(range => Array.isArray(range) && range.length === 2)
      },
      required: true
    },
    service: {
      required: true,
      type: Object
    }
  },

  computed: {
    activeColor() {
      return this.$site === this.$const.sites.galileo ? 'primaryAccent' : 'secondary'
    },
    hoursIncrement() {
      // Used to deal with cases where existing range are not synced with shop openings
      // i.e.: have 8 to 12 set but shop openings were changed to 7:30 => need to switch to 30mn intervals

      // greatest common divisor
      const gcd = (a, b) => (b === 0 ? a : gcd(b, a % b))

      return this.ranges.reduce((increment, [begin]) => {
        const offset = (begin - this.openings[0][0]) % this.increment

        if (offset === 0) {
          return increment
        }

        // gcd needs integers - multiplying by 60 should be quite safe
        return gcd(increment * 60, offset * 60) / 60
      }, this.increment)
    },
    rangeTimeComputedUnSelectable() {
      return this.openings
        .map(([begin, end]) =>
          rangeTime(begin, end, this.hoursIncrement, { hourMode: this.hour }).filter(
            opening => opening.value + this.service?.duration > end
          )
        )
        .flat()
    },
    rangeTimeComputed() {
      const ranges = this.openings
        .map(([begin, end]) => rangeTime(begin, end, this.hoursIncrement, { hourMode: this.hour }))
        .flat()

      if (ranges[ranges.length - 1].value + this.service.duration > this.openings[this.openings.length - 1][1]) {
        ranges.pop()
      }

      return ranges
    }
  },

  methods: {
    isActiveHour(value) {
      return this.ranges
        .map(([begin, end]) => ({ begin, end, interval: rangeTime(begin, end, this.hoursIncrement) }))
        .some(({ end, interval }) =>
          interval.some(hour => {
            if (hour.value + this.service.duration > end) return false
            return hour.value === value
          })
        )
    },
    unSelectHour(index, value) {
      const hourBefore = this.rangeTimeComputed[index - 1]
      const isActiveHourBefore = Boolean(hourBefore) && this.isActiveHour(hourBefore.value)
      const hourAfter = this.rangeTimeComputed[index + 1]
      const isActiveHourAfter = Boolean(hourAfter) && this.isActiveHour(hourAfter.value)

      const indexCurrentRange = this.ranges.findIndex(range => range[0] <= value && range[1] > value)

      if (isActiveHourBefore && !isActiveHourAfter) {
        this.$set(this.ranges, indexCurrentRange, [
          this.ranges[indexCurrentRange][0],
          hourBefore.value + this.service.duration
        ])
      } else if (!isActiveHourBefore && isActiveHourAfter) {
        this.$set(this.ranges, indexCurrentRange, [
          this.rangeTimeComputed[index + 1].value,
          this.ranges[indexCurrentRange][1]
        ])
      } else if (!isActiveHourBefore && !isActiveHourAfter) {
        this.ranges.splice(indexCurrentRange, 1)
      } else {
        this.ranges.splice(
          indexCurrentRange,
          1,
          [this.ranges[indexCurrentRange][0], Math.max(value, hourBefore.value + this.service.duration)],
          [this.rangeTimeComputed[index + 1].value, this.ranges[indexCurrentRange][1]]
        )
      }
    },
    selectHour(index, value) {
      const hourBefore = this.rangeTimeComputed[index - 1]
      const isActiveHourBefore = Boolean(hourBefore) && this.isActiveHour(hourBefore.value)
      const hourAfter = this.rangeTimeComputed[index + 1]
      const isActiveHourAfter = Boolean(hourAfter) && this.isActiveHour(hourAfter.value)

      const [, maxHour] = this.openings[this.openings.length - 1]
      const indexRangeBefore = hourBefore
        ? this.ranges.findIndex(range => range[0] <= hourBefore.value && range[1] > hourBefore.value)
        : -1
      const indexRangeAfter = hourAfter
        ? this.ranges.findIndex(range => range[0] <= hourAfter.value && range[1] > hourAfter.value)
        : -1

      if (isActiveHourBefore && !isActiveHourAfter) {
        let to = maxHour
        const next = this.rangeTimeComputed[index + this.service.duration / this.hoursIncrement]
        if (next) {
          to = next.value
        }

        this.$set(this.ranges, indexRangeBefore, [this.ranges[indexRangeBefore][0], to])
      } else if (!isActiveHourBefore && isActiveHourAfter) {
        this.$set(this.ranges, indexRangeAfter, [value, this.ranges[indexRangeAfter][1]])
      } else if (isActiveHourBefore && isActiveHourAfter) {
        this.ranges.splice(indexRangeBefore, 2, [
          this.ranges[indexRangeBefore][0],
          this.ranges[indexRangeAfter][this.ranges[indexRangeAfter].length - 1]
        ])
      } else {
        let to = maxHour
        const next = this.rangeTimeComputed[index + this.service.duration / this.hoursIncrement]
        if (next) {
          to = next.value
        }

        if (indexRangeAfter === -1) {
          this.ranges.push([value, to])
        } else {
          this.ranges.splice(indexRangeAfter, 0, [value, to])
        }
      }
    },
    updateRange(index, value) {
      this.$emit('edit')

      if (this.isActiveHour(value)) {
        this.unSelectHour(index, value)
      } else {
        this.selectHour(index, value)
      }
    }
  }
}
</script>

<style lang="scss">
.SelectHours {
  text-align: center;
}

.SelectHours-hour {
  display: inline-block;
  padding: 0.5em 0.8em;
  margin: 0.3em 0.2em;
  border-radius: 40px;
  font-size: 0.9rem;
  background-color: var(--v-secondary-lighten2) !important;
  color: var(--v-secondary-base);
  cursor: pointer;
  &:hover {
    background-color: var(--v-secondary-lighten1);
  }
}
</style>
